// src/pages/VehicleV2/TyreDetails.jsx

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSearch, FaTrash, FaChevronDown, FaChevronUp } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/tyreDetails";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const mapNewTyreRow = (r, idx) => ({
  id: r.fld_NewTyre_ID ?? r.fld_newtyreid ?? r.NewTyreID ?? r.TyreID ?? r.id ?? idx + 1,
  branchName: r.fld_branchname ?? r.branchname ?? r.BranchName ?? "",
  purchaseDate: r.purchaseDate ?? r.fld_purchasedate ?? r.purchasedate ?? r.PurchaseDate ?? "",
  tyreSize: r.fld_Tyresize ?? r.fld_tyresize ?? r.tyresize ?? r.TyreSize ?? "",
  vehicleCode: r.vehicleCode ?? r.fld_vehicle_code ?? r.vehicle_code ?? r.VehicleCode ?? "",
  vehicleRegNo: r.vehicleRegNo ?? r.fld_vehicle_regno ?? r.vehicle_regno ?? r.VehicleRegNo ?? "",
  tyreSerialNo: r.fld_Tyreserialno ?? r.fld_serialno ?? r.serialno ?? r.SerialNo ?? "",
  tyreMake: r.fld_TyreMake ?? r.fld_make ?? r.make ?? r.TyreMake ?? "",
  tyreModel: r.fld_TyreModel ?? r.fld_model ?? r.model ?? r.TyreModel ?? "",
  tyrePosition: r.fld_TyrePosition ?? "",
  purchaseFrom: r.fld_Purchasefrom ?? r.fld_purchasefrom ?? r.purchasefrom ?? r.PurchaseFrom ?? "",
  tyreAmount: r.fld_TyreAmounts ?? r.fld_amount ?? r.amount ?? r.TyreAmount ?? "",
});

const mapRetreadRow = (r, idx) => ({
  id: r.fld_RetrededTyre_ID ?? r.fld_retrededtyre_id ?? r.RetrededTyreID ?? r.id ?? idx + 1,
  branchName: r.fld_branchname ?? r.branchname ?? r.BranchName ?? "",
  retreadDate: r.Retereddate ?? r.RetreadDate ?? "",
  tyreSerialNo: r.fld_Tyreserialno ?? r.fld_serialno ?? r.serialno ?? r.SerialNo ?? "",
  tyreSize: r.fld_Tyresize ?? r.fld_tyresize ?? r.tyresize ?? r.TyreSize ?? "",
  tyrePosition: r.fld_TyrePosition ?? r.fld_position ?? r.position ?? r.TyrePosition ?? "",
  typeOfRetreaded: r.fld_Retrededtype ?? r.fld_typeofretreaded ?? r.typeofretreaded ?? r.TypeOfRetreaded ?? "",
  noOfRetreaded: r.fld_Noofretreded ?? r.fld_noofretreaded ?? r.noofretreaded ?? r.NoOfRetreaded ?? "",
  retreadedFrom: r.fld_Retrededfrom ?? r.fld_retreadedfrom ?? r.retreadedfrom ?? r.RetreadedFrom ?? "",
  amount: r.amount ?? r.fld_TyreAmount ?? r.Amount ?? "",
});

const matchesSearch = (row, q) => {
  if (!q) return true;
  const s = q.toLowerCase();
  return Object.values(row).some((v) => String(v ?? "").toLowerCase().includes(s));
};

const TyreDetails = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const loginUserId =
    localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const branchId = Number(
    localStorage.getItem("branchId") || localStorage.getItem("branchid") || 8
  );

  const finYear =
    localStorage.getItem("finYear") ||
    localStorage.getItem("finyear") ||
    localStorage.getItem("financialyear") ||
    "";
  const finYearId =
    localStorage.getItem("finYearId") ||
    localStorage.getItem("finyearid") ||
    localStorage.getItem("financialyearid") ||
    "";

  const requestConfig = useMemo(() => {
    const headers = { "x-branchid": branchId, "x-userid": loginUserId };
    if (finYear) headers["x-finyear"] = finYear;
    if (finYearId) {
      headers["x-finyearid"] = finYearId;
      headers["x-finyear-id"] = finYearId;
    }
    return { headers };
  }, [finYear, finYearId, branchId, loginUserId]);

  const [newSearch, setNewSearch] = useState("");
  const [retSearch, setRetSearch] = useState("");
  const [retreadTyreType, setRetreadTyreType] = useState("existing");

  const [loadingNew, setLoadingNew] = useState(false);
  const [loadingRetread, setLoadingRetread] = useState(false);
  const [newTyres, setNewTyres] = useState([]);
  const [retreadTyres, setRetreadTyres] = useState([]);

  const [selectedNewTyreIds, setSelectedNewTyreIds] = useState([]);
  const [selectedRetreadTyreIds, setSelectedRetreadTyreIds] = useState([]);

  const [showNewFilters, setShowNewFilters] = useState(true);
  const [showRetFilters, setShowRetFilters] = useState(true);

  const [newPage, setNewPage] = useState(1);
  const [newPageSize, setNewPageSize] = useState(10);
  const [retPage, setRetPage] = useState(1);
  const [retPageSize, setRetPageSize] = useState(10);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const fetchNewList = useCallback(async () => {
    try {
      setLoadingNew(true);
      const { data } = await axios.get(`${API_BASE}/new/list`, {
        params: {
          branchId,
          branchType,
          vehicleCode: "",
          vehicleName: "",
          vehicleRegNo: "",
          tyreSerialNo: "",
          tyreSize: "",
          tyreMake: "",
          search: newSearch,
        },
        ...requestConfig,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load new tyres");
      setNewTyres((data.rows || []).map(mapNewTyreRow));
      setSelectedNewTyreIds([]);
      setNewPage(1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Error loading new tyres");
      setNewTyres([]);
    } finally {
      setLoadingNew(false);
    }
  }, [branchId, branchType, newSearch, requestConfig]);

  const fetchRetreadList = useCallback(async () => {
    try {
      setLoadingRetread(true);
      const type = retreadTyreType === "new" ? "N" : "E";
      const { data } = await axios.get(`${API_BASE}/retread/list`, {
        params: {
          branchId,
          branchType,
          type,
          serialNo: "",
          tyreSize: "",
          position: "Select",
          search: retSearch,
        },
        ...requestConfig,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load retread tyres");
      setRetreadTyres((data.rows || []).map(mapRetreadRow));
      setSelectedRetreadTyreIds([]);
      setRetPage(1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Error loading retread tyres");
      setRetreadTyres([]);
    } finally {
      setLoadingRetread(false);
    }
  }, [branchId, branchType, retreadTyreType, retSearch, requestConfig]);

  useEffect(() => {
    fetchNewList();
    fetchRetreadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    fetchRetreadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retreadTyreType]);

  const filteredNew = useMemo(
    () => newTyres.filter((r) => matchesSearch(r, newSearch)),
    [newTyres, newSearch]
  );
  const filteredRet = useMemo(
    () => retreadTyres.filter((r) => matchesSearch(r, retSearch)),
    [retreadTyres, retSearch]
  );

  const newTotalPages = Math.max(1, Math.ceil(filteredNew.length / newPageSize));
  const retTotalPages = Math.max(1, Math.ceil(filteredRet.length / retPageSize));

  const pagedNewTyres = useMemo(() => {
    const start = (newPage - 1) * newPageSize;
    return filteredNew.slice(start, start + newPageSize);
  }, [filteredNew, newPage, newPageSize]);

  const pagedRetreadTyres = useMemo(() => {
    const start = (retPage - 1) * retPageSize;
    return filteredRet.slice(start, start + retPageSize);
  }, [filteredRet, retPage, retPageSize]);

  const handleEditNewTyre = (id) => {
    const tyreId = toInt(id);
    if (!tyreId) return toast.warn("Invalid Tyre ID");
    navigate(`/vehicle/AddNewTyre?tyreId=${tyreId}`);
  };

  const handleEditRetreadTyre = (id) => {
    const retreadTyreId = toInt(id);
    if (!retreadTyreId) return toast.warn("Invalid Retread Tyre ID");
    const type = retreadTyreType === "new" ? "N" : "E";
    navigate(`/vehicle/AddRetrededTyre?retreadTyreId=${retreadTyreId}&type=${type}`);
  };

  const pageIdsNew = pagedNewTyres.map((t) => t.id);
  const pageIdsRet = pagedRetreadTyres.map((t) => t.id);
  const allNewSelected =
    pageIdsNew.length > 0 && pageIdsNew.every((id) => selectedNewTyreIds.includes(id));
  const allRetSelected =
    pageIdsRet.length > 0 && pageIdsRet.every((id) => selectedRetreadTyreIds.includes(id));

  const handleSelectAllNew = () => {
    if (allNewSelected) {
      setSelectedNewTyreIds((prev) => prev.filter((id) => !pageIdsNew.includes(id)));
    } else {
      setSelectedNewTyreIds((prev) => Array.from(new Set([...prev, ...pageIdsNew])));
    }
  };

  const handleSelectAllRetread = () => {
    if (allRetSelected) {
      setSelectedRetreadTyreIds((prev) => prev.filter((id) => !pageIdsRet.includes(id)));
    } else {
      setSelectedRetreadTyreIds((prev) => Array.from(new Set([...prev, ...pageIdsRet])));
    }
  };

  const toggleSelectNew = (id) =>
    setSelectedNewTyreIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleSelectRetread = (id) =>
    setSelectedRetreadTyreIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const handleDeleteNew = async () => {
    if (selectedNewTyreIds.length === 0) {
      return toast.warn("Please select at least one tyre to delete!");
    }
    if (!window.confirm(`Delete ${selectedNewTyreIds.length} new tyre(s)?`)) return;
    try {
      const { data } = await axios.post(
        `${API_BASE}/new/delete`,
        { ids: selectedNewTyreIds, loginUserId },
        requestConfig
      );
      if (!data?.success) throw new Error(data?.message || "Delete failed");
      toast.success(data.message || "Deleted successfully");
      fetchNewList();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete error");
    }
  };

  const handleDeleteRetread = async () => {
    if (selectedRetreadTyreIds.length === 0) {
      return toast.warn("Please select at least one retread tyre to delete!");
    }
    if (!window.confirm(`Delete ${selectedRetreadTyreIds.length} retread tyre(s)?`)) return;
    try {
      const { data } = await axios.post(
        `${API_BASE}/retread/delete`,
        { ids: selectedRetreadTyreIds, loginUserId },
        requestConfig
      );
      if (!data?.success) throw new Error(data?.message || "Delete failed");
      toast.success(data.message || "Deleted successfully");
      fetchRetreadList();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete error");
    }
  };

  const PaginationBlock = ({ page, setPage, pageSize, setPageSize, total, totalPages }) => {
    const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);
    return (
      <div className="flex flex-col md:flex-row items-center justify-between gap-3 mt-4">
        <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
          Showing <b>{from}</b> - <b>{to}</b> of <b>{total}</b>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            className={inputClass.replace("w-full ", "w-auto ")}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {[5, 10, 20, 50].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
          <button
            className={`px-3 py-2 rounded-lg border ${page <= 1 ? "opacity-50 cursor-not-allowed" : ""} ${
              isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"
            }`}
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>
          <div className="text-sm font-semibold px-2">
            {page} / {totalPages}
          </div>
          <button
            className={`px-3 py-2 rounded-lg border ${
              page >= totalPages ? "opacity-50 cursor-not-allowed" : ""
            } ${isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"}`}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">TYRE DETAILS</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* NEW TYRE */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              New Tyre Details
            </h2>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate("/vehicle/AddNewTyre")}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
              >
                <FaPlus /> Add New Tyre
              </button>
              <button
                onClick={() => setShowNewFilters((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showNewFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showNewFilters ? "Hide Search" : "Show Search"}
              </button>
            </div>
          </div>

          {showNewFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search New Tyres
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setNewSearch("");
                      setNewPage(1);
                      fetchNewList();
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>
                  <button
                    onClick={fetchNewList}
                    disabled={loadingNew}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
                  >
                    <FaSearch />
                    {loadingNew ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>
              <div>
                <label className={labelClass}>Search</label>
                <input
                  value={newSearch}
                  onChange={(e) => {
                    setNewSearch(e.target.value);
                    setNewPage(1);
                  }}
                  onKeyDown={(e) => e.key === "Enter" && fetchNewList()}
                  className={inputClass}
                  placeholder="Search serial no, vehicle, size, make, branch..."
                />
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg mb-10">
            {loadingNew ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                Loading...
              </div>
            ) : filteredNew.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800 text-gray-400" : "bg-gray-50 text-gray-500"}`}>
                No records found
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      {[
                        "S.No",
                        "Branch",
                        "Purchase Date",
                        "Tyre Size",
                        "Vehicle Code",
                        "Vehicle RegNo",
                        "Serial No",
                        "Make",
                        "Model",
                        "Position",
                        "Purchase From",
                        "Amount",
                        "Edit",
                      ].map((h) => (
                        <th key={h} className="px-4 py-4 text-left text-sm font-semibold whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                      <th className="px-4 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={allNewSelected}
                          onChange={handleSelectAllNew}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {pagedNewTyres.map((tyre, idx) => (
                      <tr
                        key={tyre.id}
                        className={`${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className="px-4 py-3 text-sm">{(newPage - 1) * newPageSize + idx + 1}</td>
                        <td className="px-4 py-3 text-sm">{tyre.branchName}</td>
                        <td className="px-4 py-3 text-sm">{tyre.purchaseDate}</td>
                        <td className="px-4 py-3 text-sm">{tyre.tyreSize}</td>
                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {tyre.vehicleCode}
                        </td>
                        <td className="px-4 py-3 text-sm">{tyre.vehicleRegNo}</td>
                        <td className="px-4 py-3 text-sm">{tyre.tyreSerialNo}</td>
                        <td className="px-4 py-3 text-sm">{tyre.tyreMake}</td>
                        <td className="px-4 py-3 text-sm">{tyre.tyreModel}</td>
                        <td className="px-4 py-3 text-sm">{tyre.tyrePosition}</td>
                        <td className="px-4 py-3 text-sm">{tyre.purchaseFrom}</td>
                        <td className="px-4 py-3 text-sm">{tyre.tyreAmount}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleEditNewTyre(tyre.id)}
                            className="text-yellow-500 hover:text-yellow-600 text-lg"
                            title="Edit"
                          >
                            ✏️
                          </button>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedNewTyreIds.includes(tyre.id)}
                            onChange={() => toggleSelectNew(tyre.id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-4">
                  <button
                    onClick={handleDeleteNew}
                    disabled={selectedNewTyreIds.length === 0}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow text-white ${
                      selectedNewTyreIds.length === 0
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                  >
                    <FaTrash /> Remove
                  </button>
                </div>
              </>
            )}
          </div>

          <PaginationBlock
            page={newPage}
            setPage={setNewPage}
            pageSize={newPageSize}
            setPageSize={setNewPageSize}
            total={filteredNew.length}
            totalPages={newTotalPages}
          />

          {/* RETREAD */}
          <div className="flex justify-between items-center mt-12 mb-6 flex-wrap gap-3">
            <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Retread Tyre Details
            </h2>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate("/vehicle/AddRetrededTyre")}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
              >
                <FaPlus /> Add Retread Tyre
              </button>
              <button
                onClick={() => setShowRetFilters((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showRetFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showRetFilters ? "Hide Search" : "Show Search"}
              </button>
            </div>
          </div>

          {showRetFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Retread Tyres
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setRetSearch("");
                      setRetPage(1);
                      fetchRetreadList();
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>
                  <button
                    onClick={fetchRetreadList}
                    disabled={loadingRetread}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
                  >
                    <FaSearch />
                    {loadingRetread ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Search</label>
                  <input
                    value={retSearch}
                    onChange={(e) => {
                      setRetSearch(e.target.value);
                      setRetPage(1);
                    }}
                    onKeyDown={(e) => e.key === "Enter" && fetchRetreadList()}
                    className={inputClass}
                    placeholder="Search serial no, size, position, vendor..."
                  />
                </div>
                <div>
                  <label className={labelClass}>Retread Source</label>
                  <div className="flex gap-6 pt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={retreadTyreType === "existing"}
                        onChange={() => setRetreadTyreType("existing")}
                        className="w-4 h-4 text-blue-600"
                      />
                      Existing Tyre
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={retreadTyreType === "new"}
                        onChange={() => setRetreadTyreType("new")}
                        className="w-4 h-4 text-blue-600"
                      />
                      New Tyre
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loadingRetread ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                Loading...
              </div>
            ) : filteredRet.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800 text-gray-400" : "bg-gray-50 text-gray-500"}`}>
                No records found
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      {[
                        "S.No",
                        "Branch",
                        "Retread Date",
                        "Serial No",
                        "Size",
                        "Position",
                        "Type",
                        "No of Retread",
                        "Retreaded From",
                        "Amount",
                        "Edit",
                      ].map((h) => (
                        <th key={h} className="px-4 py-4 text-left text-sm font-semibold whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                      <th className="px-4 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={allRetSelected}
                          onChange={handleSelectAllRetread}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {pagedRetreadTyres.map((tyre, idx) => (
                      <tr
                        key={tyre.id}
                        className={`${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className="px-4 py-3 text-sm">{(retPage - 1) * retPageSize + idx + 1}</td>
                        <td className="px-4 py-3 text-sm">{tyre.branchName}</td>
                        <td className="px-4 py-3 text-sm">{tyre.retreadDate}</td>
                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {tyre.tyreSerialNo}
                        </td>
                        <td className="px-4 py-3 text-sm">{tyre.tyreSize}</td>
                        <td className="px-4 py-3 text-sm">{tyre.tyrePosition}</td>
                        <td className="px-4 py-3 text-sm">{tyre.typeOfRetreaded}</td>
                        <td className="px-4 py-3 text-sm">{tyre.noOfRetreaded}</td>
                        <td className="px-4 py-3 text-sm">{tyre.retreadedFrom}</td>
                        <td className="px-4 py-3 text-sm">{tyre.amount}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleEditRetreadTyre(tyre.id)}
                            className="text-yellow-500 hover:text-yellow-600 text-lg"
                            title="Edit"
                          >
                            ✏️
                          </button>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedRetreadTyreIds.includes(tyre.id)}
                            onChange={() => toggleSelectRetread(tyre.id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-4">
                  <button
                    onClick={handleDeleteRetread}
                    disabled={selectedRetreadTyreIds.length === 0}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow text-white ${
                      selectedRetreadTyreIds.length === 0
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                  >
                    <FaTrash /> Remove
                  </button>
                </div>
              </>
            )}
          </div>

          <PaginationBlock
            page={retPage}
            setPage={setRetPage}
            pageSize={retPageSize}
            setPageSize={setRetPageSize}
            total={filteredRet.length}
            totalPages={retTotalPages}
          />

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default TyreDetails;